/**
 * @fileoverview Complete report-template, generated-report, delivery, and
 *               document-version schemas for the complex-inspections contract
 *               bundle (document 14).
 * @module import-core/contracts/complex-inspections/reportSchemas
 *
 * Responsibilities:
 * - Define `reportTemplates` (including the two complex section types and the
 *   published-complexVisit participant-section rule).
 * - Define the `GeneratedReportSource` discriminated union on
 *   `reportDataScope`, the complete `generatedReports` document, delivery
 *   records/groups, and write-once `generatedDocumentVersions`.
 * - Enforce top-level projection fields matching their source values.
 *
 * Dependencies:
 * - `zod` v3; `./sharedSchemas`; `./contactSchemas` (delivery snapshots).
 *
 * Architecture:
 * - Persisted-boundary schemas. `documentType` remains `inspectionReport` —
 *   `complex` is not a document type, and no invoice collection is invented.
 *
 * Notes:
 * - `version` exists only where document 14 declares it (report templates,
 *   generated reports); delivery records/groups carry no `version` field.
 */

import { z } from 'zod';
import {
  objectIdLikeSchema,
  isoDateLikeSchema,
  entityCategorySchema,
  postalAddressSchema,
} from './sharedSchemas';
import { inspectionDeliveryRecipientSnapshotSchema } from './contactSchemas';

// ---------------------------------------------------------------------------
// Shared report enums
// ---------------------------------------------------------------------------

export const documentTypeSchema = z.enum(['inspectionReport', 'permit']);
export const reportDataScopeSchema = z.enum(['singleCase', 'complexVisit']);
export const reportTemplateStatusSchema = z.enum(['draft', 'published', 'archived']);
export const deliveryMethodSchema = z.enum(['email', 'mail', 'post', 'text', 'stannp']);
export const shareVisibilitySchema = z.enum(['private', 'organization', 'public']);
export const correspondencePurposeSchema = z.enum(['standalone', 'defaultComplex', 'oneOffEntity']);

// ---------------------------------------------------------------------------
// Report template sections (document 14 §2)
// ---------------------------------------------------------------------------

/**
 * @contract reportSectionTypeSchema
 * Purpose: Canonical persisted report section discriminator.
 * Fields: Standard single-case section IDs plus complex visit summary/participant IDs.
 * Rules: Host validators consume this package-owned enum; complex section configs remain strict.
 * Sync: REST, local-db, sync, and renderer section IDs.
 */
export const reportSectionTypeSchema = z.enum([
  'inspectionInfo',
  'entityInfo',
  'responsibleParty',
  'legalText',
  'questionsResponses',
  'violations',
  'additionalViolations',
  'inspectorNotes',
  'photos',
  'signatures',
  'billing',
  'permitInfo',
  'reinspectionInfo',
  'correctionSummary',
  'customText',
  'seriesHistory',
  'coverPage',
  'complexVisitSummary',
  'complexParticipants',
]);

/**
 * @contract complexVisitSummarySectionConfigSchema
 * Purpose: Strict persisted options for the complex visit summary section.
 * Fields: Complex name, anchor, date, participant counts, standalone summary flags.
 * Rules: No unsupported title or unknown config fields.
 * Sync: Shared by report schema, local transform, and renderer config.
 */
export const complexVisitSummarySectionConfigSchema = z
  .object({
    showComplexName: z.boolean(),
    showAnchor: z.boolean(),
    showVisitDate: z.boolean(),
    showParticipantCounts: z.boolean(),
    showStandaloneSummary: z.boolean(),
  })
  .strict();

const existingReportSectionTypeSchema = reportSectionTypeSchema.exclude([
  'complexVisitSummary',
  'complexParticipants',
]);

const existingReportSectionSchema = z
  .object({
    id: z.string(),
    type: existingReportSectionTypeSchema,
    title: z.string().optional(),
    titleOverride: z.string().optional(),
    order: z.number(),
    enabled: z.boolean(),
    config: z.record(z.unknown()).optional(),
  })
  .strict();

/** @contract Strict persisted complexVisitSummary section envelope. */
export const complexVisitSummarySectionSchema = z
  .object({
    id: z.string(),
    type: z.literal('complexVisitSummary'),
    order: z.number(),
    enabled: z.boolean(),
    config: complexVisitSummarySectionConfigSchema,
  })
  .strict();

/**
 * @contract complexParticipantsSectionConfigSchema
 * Purpose: Strict persisted options for per-participant complex rendering.
 * Fields: Inclusion, page-break, entity metadata, nested detail, and sort flags.
 * Rules: Unknown fields and unsupported title values are rejected.
 * Sync: Shared by report schema, local transform, and renderer config.
 */
export const complexParticipantsSectionConfigSchema = z
  .object({
    includeRootEntity: z.boolean(),
    includeGovernedMembers: z.boolean(),
    includeStandaloneSummary: z.boolean(),
    startEachEntityOnNewPage: z.boolean(),
    showEntityHeader: z.boolean(),
    showEntityAddress: z.boolean(),
    showEntityStatus: z.boolean(),
    showQuestionsResponses: z.boolean(),
    showViolations: z.boolean(),
    showCorrections: z.boolean(),
    showPermits: z.boolean(),
    showAssets: z.boolean(),
    showPhotos: z.boolean(),
    showSignatures: z.boolean(),
    entitySort: z.enum(['visitOrder', 'entityName', 'entityCategory']),
  })
  .strict();

const complexParticipantsSectionSchema = z
  .object({
    id: z.string(),
    type: z.literal('complexParticipants'),
    order: z.number(),
    enabled: z.boolean(),
    config: complexParticipantsSectionConfigSchema,
  })
  .strict();

export const reportSectionSchema = z.discriminatedUnion('type', [
  complexVisitSummarySectionSchema,
  complexParticipantsSectionSchema,
  existingReportSectionSchema,
]);

// ---------------------------------------------------------------------------
// Report template config blocks (document 14 §2)
// ---------------------------------------------------------------------------

const sharingMetadataSchema = z
  .object({
    sourceType: z.enum(['system', 'organization', 'department']),
    sourceId: z.string(),
    sourceItemId: z.string(),
    sourceVersion: z.number(),
    inheritanceMode: z.enum(['linked', 'forked', 'independent']),
    sharedAt: isoDateLikeSchema,
    forkedAt: isoDateLikeSchema.optional(),
    lastSyncedAt: isoDateLikeSchema.optional(),
    canFork: z.boolean(),
    canPullUpdates: z.boolean(),
  })
  .strict();

const documentDeliveryProfileSchema = z
  .object({
    designedFor: z.enum(['windowEnvelope', 'email', 'either']),
    supportedMethods: z.array(deliveryMethodSchema),
    preferredMethod: deliveryMethodSchema.optional(),
  })
  .strict();

const coverPageConfigSchema = z
  .object({
    enabled: z.boolean(),
    showLogo: z.boolean(),
    logoSize: z.enum(['small', 'medium', 'large']),
    showDepartmentName: z.boolean(),
    showReportTitle: z.boolean(),
    reportTitleOverride: z.string().optional(),
    showEntityName: z.boolean(),
    showEntityAddress: z.boolean(),
    showInspectionDate: z.boolean(),
    showInspectorName: z.boolean(),
    showResultSummary: z.boolean(),
    resultSummaryStyle: z.enum(['badge', 'text', 'icon']),
    customContent: z.string().optional(),
  })
  .strict();

const headerConfigSchema = z
  .object({
    showLogo: z.boolean(),
    logoPosition: z.enum(['left', 'center', 'right']),
    showDepartmentName: z.boolean(),
    showDepartmentAddress: z.boolean(),
    showDepartmentPhone: z.boolean(),
    showReportTitle: z.boolean(),
    reportTitle: z.string().optional(),
    customSubtitle: z.string().optional(),
    showReportNumber: z.boolean(),
    showDate: z.boolean(),
    dateFormat: z.enum(['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', 'long']),
  })
  .strict();

const footerConfigSchema = z
  .object({
    showPageNumbers: z.boolean(),
    pageNumberFormat: z.enum(['page-of-total', 'page-only']),
    showDepartmentName: z.boolean(),
    showConfidentialityNotice: z.boolean(),
    confidentialityText: z.string().optional(),
    customFooterText: z.string().optional(),
    showGeneratedDate: z.boolean(),
  })
  .strict();

const signaturesConfigSchema = z
  .object({
    inspectorSignature: z
      .object({
        enabled: z.boolean(),
        label: z.string(),
        showName: z.boolean(),
        showTitle: z.boolean(),
        showDate: z.boolean(),
        showBadgeNumber: z.boolean(),
      })
      .strict(),
    responsiblePartySignature: z
      .object({
        enabled: z.boolean(),
        label: z.string(),
        showName: z.boolean(),
        showDate: z.boolean(),
      })
      .strict(),
  })
  .strict();

const photoConfigSchema = z
  .object({
    placement: z.enum(['inline', 'gallery', 'both', 'none']),
    galleryTitle: z.string().optional(),
    maxPhotosPerViolation: z.number().optional(),
    maxGalleryPhotos: z.number().optional(),
    showCaptions: z.boolean(),
    showTimestamps: z.boolean(),
    showLocation: z.boolean(),
    photoSize: z.enum(['small', 'medium', 'large']),
    photosPerRow: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  })
  .strict();

const autoHideRulesSchema = z
  .object({
    hideViolationsIfNone: z.boolean(),
    hidePhotosIfNone: z.boolean(),
    hideNotesIfEmpty: z.boolean(),
    hideQuestionsIfNone: z.boolean(),
    hideCoverPageIfNoViolations: z.boolean(),
    customRules: z
      .array(
        z
          .object({
            sectionId: z.string(),
            hideWhen: z.enum(['empty', 'noViolations', 'noPhotos', 'always']),
          })
          .strict(),
      )
      .optional(),
  })
  .strict();

const legalTextConfigSchema = z
  .object({
    headerLegalText: z.string().optional(),
    footerLegalText: z.string().optional(),
    violationsDisclaimer: z.string().optional(),
    permitDisclaimer: z.string().optional(),
    appealRightsText: z.string().optional(),
    customLegalBlocks: z
      .array(
        z
          .object({
            id: z.string(),
            title: z.string().optional(),
            content: z.string(),
            position: z.enum([
              'beforeViolations',
              'afterViolations',
              'beforeSignatures',
              'afterSignatures',
              'endOfReport',
            ]),
          })
          .strict(),
      )
      .optional(),
  })
  .strict();

const permitConfigSchema = z
  .object({
    showPermitNumber: z.boolean(),
    permitNumberPrefix: z.string().optional(),
    showIssuanceDate: z.boolean(),
    showExpirationDate: z.boolean(),
    showConditions: z.boolean(),
    defaultConditions: z.array(z.string()).optional(),
    showPaymentSummary: z.boolean(),
    showInspectionSummary: z.boolean(),
  })
  .strict();

/**
 * @contract ReportTemplateDataScopeIssue
 * Purpose: Sanitized blocking issue for report-template publish scope validation.
 * Fields: code identifies the contract rule; path points to the untrusted field;
 *   message is safe for API/UI display.
 * Rules: INVALID_REPORT_DATA_SCOPE rejects any present scope outside the exact
 *   singleCase/complexVisit union; topology codes apply only to valid scopes.
 * Sync: Transient validation result shared by API, local-first publish, and schema.
 */
export interface ReportTemplateDataScopeIssue {
  code:
    | 'INVALID_REPORT_DATA_SCOPE'
    | 'INVALID_DOCUMENT_TYPE'
    | 'MISSING_COMPLEX_PARTICIPANTS'
    | 'MULTIPLE_COMPLEX_PARTICIPANTS'
    | 'COMPLEX_SECTION_ON_SINGLE_CASE';
  path: Array<string | number>;
  message: string;
}

/**
 * @contract ReportTemplateDataScopeValidation
 * Purpose: Complete pure validation result for a report-template publish candidate.
 * Fields: valid, effectiveScope, and sanitized issues.
 * Rules: absent scope is singleCase; invalid present scope is never transitional.
 * Sync: Transient result shared by API, local-first publish, and persisted schema.
 */
export interface ReportTemplateDataScopeValidation {
  valid: boolean;
  effectiveScope: 'singleCase' | 'complexVisit';
  issues: ReportTemplateDataScopeIssue[];
}

/**
 * @process validateReportTemplateDataScopeForPublish
 * Steps: Normalize absent scope to singleCase; reject invalid present values;
 *   inspect enabled sections using original indexes; apply published topology rules.
 * Inputs: Untrusted reportDataScope, documentType, status, and sections candidate.
 * Outputs: Effective scope plus sanitized issues; no input mutation.
 * Failure Handling: Returns valid=false for contract violations and never throws.
 * Idempotency: REQUIRED — pure deterministic function.
 */
export function validateReportTemplateDataScopeForPublish(template: {
  reportDataScope?: unknown;
  documentType?: unknown;
  status?: unknown;
  sections?: unknown;
}): ReportTemplateDataScopeValidation {
  const hasScope = template.reportDataScope !== undefined;
  const scopeIsValid = !hasScope || template.reportDataScope === 'singleCase' || template.reportDataScope === 'complexVisit';
  const effectiveScope = template.reportDataScope === 'complexVisit' ? 'complexVisit' : 'singleCase';
  const issues: ReportTemplateDataScopeIssue[] = [];
  if (!scopeIsValid) {
    issues.push({
      code: 'INVALID_REPORT_DATA_SCOPE',
      path: ['reportDataScope'],
      message: 'reportDataScope must be exactly singleCase or complexVisit',
    });
    return { valid: false, effectiveScope: 'singleCase', issues };
  }
  if (template.status !== 'published') return { valid: true, effectiveScope, issues };

  const sections = Array.isArray(template.sections) ? template.sections : [];
  const enabledComplexSections = sections.flatMap((section, index) => {
    if (typeof section !== 'object' || section === null || (section as Record<string, unknown>).enabled !== true) return [];
    return [{ section: section as Record<string, unknown>, index }];
  });
  const participantSections = enabledComplexSections.filter(({ section }) => section.type === 'complexParticipants');

  if (effectiveScope === 'complexVisit') {
    if (template.documentType !== 'inspectionReport') {
      issues.push({
        code: 'INVALID_DOCUMENT_TYPE',
        path: ['documentType'],
        message: 'A published complexVisit template must use document type inspectionReport',
      });
    }
    if (participantSections.length === 0) {
      issues.push({
        code: 'MISSING_COMPLEX_PARTICIPANTS',
        path: ['sections'],
        message: 'A published complexVisit template must contain one enabled complexParticipants section',
      });
    } else if (participantSections.length > 1) {
      issues.push({
        code: 'MULTIPLE_COMPLEX_PARTICIPANTS',
        path: ['sections'],
        message: 'A published complexVisit template must contain exactly one enabled complexParticipants section',
      });
    }
  } else {
    for (const { section, index } of enabledComplexSections) {
      if (section.type === 'complexVisitSummary' || section.type === 'complexParticipants') {
        issues.push({
          code: 'COMPLEX_SECTION_ON_SINGLE_CASE',
          path: ['sections', index, 'type'],
          message: 'Complex visit sections are not allowed on a published singleCase template',
        });
      }
    }
  }

  return { valid: issues.length === 0, effectiveScope, issues };
}

/**
 * @contract reportTemplateDocumentSchema
 * Purpose: Complete `reportTemplates` document. `reportDataScope` defaults to
 *   `singleCase` on legacy data; a published `complexVisit` template must be
 *   an `inspectionReport` containing one enabled `complexParticipants`
 *   section.
 */
export const reportTemplateDocumentSchema = z
  .object({
    _id: objectIdLikeSchema,
    schemaVersion: z.literal(1),

    name: z.string(),
    description: z.string().optional(),
    documentType: documentTypeSchema,
    reportDataScope: reportDataScopeSchema.default('singleCase'),

    version: z.number(),
    status: reportTemplateStatusSchema,
    publishedAt: isoDateLikeSchema.optional(),
    publishedBy: objectIdLikeSchema.optional(),

    isSystemSeed: z.boolean().optional(),
    systemSeedId: z.string().optional(),
    derivedFrom: z
      .object({
        reportTemplateId: objectIdLikeSchema,
        reportTemplateVersion: z.number(),
        sourceLevel: z.enum(['system', 'organization', 'department']),
        sourceDepartmentId: objectIdLikeSchema.optional(),
        sourceOrganizationId: objectIdLikeSchema.optional(),
        copiedAt: isoDateLikeSchema,
        copiedBy: objectIdLikeSchema,
      })
      .strict()
      .optional(),

    layoutVariant: z.string(),
    pageSize: z.enum(['letter', 'a4', 'legal']),
    orientation: z.enum(['portrait', 'landscape']),

    coverPage: coverPageConfigSchema.optional(),
    header: headerConfigSchema,
    sections: z.array(reportSectionSchema),
    footer: footerConfigSchema,
    signatures: signaturesConfigSchema,
    photoConfig: photoConfigSchema,
    autoHideRules: autoHideRulesSchema,
    legalText: legalTextConfigSchema.optional(),
    permitConfig: permitConfigSchema.optional(),
    deliveryProfile: documentDeliveryProfileSchema.optional(),

    advancedBinding: z
      .object({
        mode: z.enum(['standard', 'advancedLinked']),
        linkedInspectionTemplateId: objectIdLikeSchema.optional(),
        linkedInspectionTemplateVersion: z.number().optional(),
        linkedInspectionTemplateName: z.string().optional(),
        lockTemplateLink: z.boolean().optional(),
        fieldCatalogVersion: z.number().optional(),
      })
      .strict()
      .optional(),
    advancedFields: z
      .array(
        z
          .object({
            id: z.string(),
            label: z.string(),
            sourceType: z.enum(['templateResponse', 'templateField', 'systemMergeField']),
            bindingKey: z.string(),
            fieldId: z.string().optional(),
            formId: z.string().optional(),
            formName: z.string().optional(),
            fieldLabel: z.string().optional(),
            renderMode: z.enum([
              'inlineText',
              'block',
              'tableCell',
              'checkbox',
              'badge',
              'photo',
              'richText',
            ]),
            fallbackText: z.string().optional(),
            includeWhenEmpty: z.boolean().optional(),
            formatting: z.record(z.unknown()).optional(),
          })
          .strict(),
      )
      .optional(),

    sharing: sharingMetadataSchema.optional(),
    visibility: shareVisibilitySchema.optional(),

    createdAt: isoDateLikeSchema,
    createdBy: objectIdLikeSchema,
    updatedAt: isoDateLikeSchema,
    updatedBy: objectIdLikeSchema,
    _deleted: z.boolean(),
    deletedAt: isoDateLikeSchema.optional(),
    deletedBy: objectIdLikeSchema.optional(),
    _syncSeq: z.number().optional(),
  })
  .strict()
  .superRefine((doc, ctx) => {
    const validation = validateReportTemplateDataScopeForPublish(doc);
    for (const issue of validation.issues) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: issue.path,
        message: issue.message,
      });
    }
  });

// ---------------------------------------------------------------------------
// Generated report source union (document 14 §3)
// ---------------------------------------------------------------------------

const reportContributorRefSchema = z
  .object({
    caseId: objectIdLikeSchema,
    eventIndex: z.number().int(),
    entityId: objectIdLikeSchema,
    entityCategory: entityCategorySchema,
    entityName: z.string(),
    entityAddress: z.string().optional(),
    participationMode: z.literal('complex-governed'),
    caseUpdatedAt: isoDateLikeSchema,
    eventCompletedAt: isoDateLikeSchema,
    responseSnapshotHash: z.string(),
    violationStateHash: z.string(),
    attachmentStateHash: z.string(),
  })
  .strict();

export const singleCaseGeneratedReportSourceSchema = z
  .object({
    schemaVersion: z.literal(1),
    reportDataScope: z.literal('singleCase'),
    correspondencePurpose: z.enum(['standalone', 'oneOffEntity']),
    caseId: objectIdLikeSchema,
    eventIndex: z.number().int(),
    entityId: objectIdLikeSchema,
    entityCategory: entityCategorySchema,
    entityName: z.string(),
    complexContext: z
      .object({
        visitId: objectIdLikeSchema,
        groupId: objectIdLikeSchema,
        participationMode: z.enum(['standalone', 'complex-governed']),
      })
      .strict()
      .optional(),
    sourceStateChecksum: z.string(),
  })
  .strict();

export const complexVisitGeneratedReportSourceSchema = z
  .object({
    schemaVersion: z.literal(1),
    reportDataScope: z.literal('complexVisit'),
    correspondencePurpose: z.literal('defaultComplex'),
    visitId: objectIdLikeSchema,
    groupId: objectIdLikeSchema,
    groupName: z.string(),
    rootCaseId: objectIdLikeSchema,
    rootEventIndex: z.number().int(),
    contributingEventRefs: z.array(reportContributorRefSchema),
    sourceStateChecksum: z.string(),
  })
  .strict();

/**
 * @contract generatedReportSourceSchema
 * Purpose: Discriminated union on `reportDataScope`. Mixed/partial variants
 *   are rejected.
 */
export const generatedReportSourceSchema = z.discriminatedUnion('reportDataScope', [
  singleCaseGeneratedReportSourceSchema,
  complexVisitGeneratedReportSourceSchema,
]);

// ---------------------------------------------------------------------------
// generatedReports document (document 14 §4)
// ---------------------------------------------------------------------------

/**
 * @contract generatedReportDocumentSchema
 * Purpose: Complete generated artifact metadata plus immutable
 *   source/contributor snapshot. Top-level query projections
 *   (`reportDataScope`, `correspondencePurpose`, `caseId`, `eventIndex`,
 *   `visitId`, `groupId`) must equal the corresponding source values —
 *   enforced by refinement.
 */
export const generatedReportDocumentSchema = z
  .object({
    _id: objectIdLikeSchema,
    schemaVersion: z.literal(1),
    name: z.string(),

    source: generatedReportSourceSchema,

    reportDataScope: reportDataScopeSchema,
    correspondencePurpose: correspondencePurposeSchema,
    caseId: objectIdLikeSchema,
    eventIndex: z.number().int(),
    visitId: objectIdLikeSchema.optional(),
    groupId: objectIdLikeSchema.optional(),

    reportTemplateId: objectIdLikeSchema,
    reportTemplateVersion: z.number(),
    triggerId: z.string().optional(),

    documentType: z.literal('inspectionReport'),
    reportNumber: z.string(),

    blobUrl: z.string(),
    blobPath: z.string(),
    fileSizeBytes: z.number(),
    pageCount: z.number(),
    htmlBlobUrl: z.string().optional(),

    generatedAt: isoDateLikeSchema,
    generatedBy: objectIdLikeSchema,
    generationMode: z.enum(['automatic', 'manual', 'batch', 'complexVisit']),
    generationDurationMs: z.number(),
    generationIdentity: z.string(),
    generationContext: z
      .object({
        documentMode: z.enum(['standard', 'advancedLinked']).optional(),
        usedTemplateSnapshot: z.boolean().optional(),
        linkedInspectionTemplateId: objectIdLikeSchema.optional(),
        fieldBindingSummary: z
          .array(
            z
              .object({
                bindingKey: z.string(),
                resolvedFrom: z.enum(['response', 'snapshot-default', 'system-field', 'fallback']),
              })
              .strict(),
          )
          .optional(),
      })
      .strict()
      .optional(),

    emailContext: z
      .object({
        entityName: z.string().optional(),
        entityAddress: z.string().optional(),
        complexName: z.string().optional(),
        inspectionDate: isoDateLikeSchema.optional(),
        inspectorName: z.string().optional(),
        departmentName: z.string().optional(),
        departmentPhone: z.string().optional(),
        departmentEmail: z.string().optional(),
        parcelNumber: z.string().optional(),
      })
      .strict()
      .optional(),
    emailTemplateId: objectIdLikeSchema.nullable().optional(),

    distributionRecipients: z.array(
      z
        .object({
          contactId: objectIdLikeSchema.optional(),
          email: z.string(),
          name: z.string().optional(),
          source: z.enum([
            'responsibleParty',
            'entityContact',
            'complexContact',
            'configured',
            'inspector',
            'departmentAdmin',
          ]),
        })
        .strict(),
    ),
    recipients: z.array(
      z
        .object({
          contactId: objectIdLikeSchema.optional(),
          name: z.string(),
          email: z.string(),
          source: z.string(),
          emailSentAt: isoDateLikeSchema.optional(),
          emailStatus: z.enum(['pending', 'sent', 'failed', 'bounced']),
          secureLinkToken: z.string().optional(),
          secureLinkExpiresAt: isoDateLikeSchema.optional(),
        })
        .strict(),
    ),
    distributionStatus: z.enum(['pending', 'ready', 'distributed', 'partial', 'failed', 'skipped']),
    distributedAt: isoDateLikeSchema.optional(),
    deliveryStatus: z.enum(['generated', 'sent', 'delivered', 'failed']),

    version: z.number(),
    createdAt: isoDateLikeSchema,
    createdBy: objectIdLikeSchema,
    updatedAt: isoDateLikeSchema,
    updatedBy: objectIdLikeSchema,
    _deleted: z.boolean(),
    deletedAt: isoDateLikeSchema.optional(),
    deletedBy: objectIdLikeSchema.optional(),
  })
  .strict()
  .superRefine((doc, ctx) => {
    const source = doc.source;
    if (doc.reportDataScope !== source.reportDataScope) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reportDataScope'],
        message: 'top-level reportDataScope must equal source.reportDataScope',
      });
    }
    if (doc.correspondencePurpose !== source.correspondencePurpose) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['correspondencePurpose'],
        message: 'top-level correspondencePurpose must equal source.correspondencePurpose',
      });
    }
    const sourceCaseId = source.reportDataScope === 'complexVisit' ? source.rootCaseId : source.caseId;
    const sourceEventIndex =
      source.reportDataScope === 'complexVisit' ? source.rootEventIndex : source.eventIndex;
    if (String(doc.caseId) !== String(sourceCaseId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['caseId'],
        message: 'top-level caseId must equal source.caseId or source.rootCaseId',
      });
    }
    if (doc.eventIndex !== sourceEventIndex) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['eventIndex'],
        message: 'top-level eventIndex must equal source.eventIndex or source.rootEventIndex',
      });
    }
    const sourceVisitId = source.reportDataScope === 'complexVisit' ? source.visitId : undefined;
    const sourceGroupId = source.reportDataScope === 'complexVisit' ? source.groupId : undefined;
    if (String(doc.visitId ?? '') !== String(sourceVisitId ?? '')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['visitId'],
        message: 'top-level visitId must equal source.visitId',
      });
    }
    if (String(doc.groupId ?? '') !== String(sourceGroupId ?? '')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['groupId'],
        message: 'top-level groupId must equal source.groupId',
      });
    }
  });

// ---------------------------------------------------------------------------
// Delivery records / groups / document versions (document 14 §6–§7)
// ---------------------------------------------------------------------------

/**
 * @contract generatedReportSourceSummarySchema
 * Purpose: Small billing/source summary copied onto delivery records and
 *   document versions — never a second copy of the report.
 */
export const generatedReportSourceSummarySchema = z
  .object({
    reportDataScope: reportDataScopeSchema,
    correspondencePurpose: correspondencePurposeSchema,
    billingGroupKey: z.string(),
    visitId: objectIdLikeSchema.optional(),
    groupId: objectIdLikeSchema.optional(),
    rootCaseId: objectIdLikeSchema.optional(),
    rootEventIndex: z.number().int().optional(),
    caseId: objectIdLikeSchema.optional(),
    eventIndex: z.number().int().optional(),
  })
  .strict()
  .superRefine((source, ctx) => {
    const fail = (path: (string | number)[], message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
    if (source.reportDataScope === 'complexVisit') {
      if (source.correspondencePurpose !== 'defaultComplex') {
        fail(['correspondencePurpose'], "complexVisit source summary requires correspondencePurpose 'defaultComplex'");
      }
      for (const key of ['visitId', 'groupId', 'rootCaseId', 'rootEventIndex'] as const) {
        if (source[key] === undefined) fail([key], `complexVisit source summary requires ${key}`);
      }
      for (const key of ['caseId', 'eventIndex'] as const) {
        if (source[key] !== undefined) fail([key], `complexVisit source summary must not carry ${key}`);
      }
      if (
        source.visitId !== undefined &&
        source.billingGroupKey !== `complex-visit:${String(source.visitId)}`
      ) {
        fail(['billingGroupKey'], "complexVisit billingGroupKey must equal 'complex-visit:' + visitId");
      }
    } else {
      if (source.correspondencePurpose === 'defaultComplex') {
        fail(['correspondencePurpose'], "singleCase source summary forbids correspondencePurpose 'defaultComplex'");
      }
      if (source.caseId === undefined) fail(['caseId'], 'singleCase source summary requires caseId');
      if (source.eventIndex === undefined) fail(['eventIndex'], 'singleCase source summary requires eventIndex');
      for (const key of ['visitId', 'groupId', 'rootCaseId', 'rootEventIndex'] as const) {
        if (source[key] !== undefined) fail([key], `singleCase source summary must not carry ${key}`);
      }
      if (
        source.caseId !== undefined &&
        source.eventIndex !== undefined &&
        source.billingGroupKey !== `case-event:${String(source.caseId)}:${source.eventIndex}`
      ) {
        fail(['billingGroupKey'], "singleCase billingGroupKey must equal 'case-event:' + caseId + ':' + eventIndex");
      }
    }
  });

/**
 * @contract inspectionDeliveryRecordDocumentSchema
 * Purpose: Operational recipients/channels/status for one generated report.
 *   One generated complex report produces one active delivery record.
 */
export const inspectionDeliveryRecordDocumentSchema = z
  .object({
    _id: objectIdLikeSchema,
    schemaVersion: z.literal(1),

    generatedDocumentId: objectIdLikeSchema,
    reportSource: generatedReportSourceSummarySchema,

    caseId: objectIdLikeSchema,
    caseNumber: z.string().optional(),
    caseEntityName: z.string(),
    eventIndex: z.number().int(),
    eventType: z.string(),

    inspectionAccountId: objectIdLikeSchema.optional(),
    inspectionAccountName: z.string().optional(),
    entityId: objectIdLikeSchema.optional(),
    entityName: z.string().optional(),
    entityAddress: z.string().optional(),

    reportNumber: z.string().optional(),
    reportName: z.string().optional(),

    responsiblePartyContactId: objectIdLikeSchema.optional(),
    responsiblePartySnapshot: inspectionDeliveryRecipientSnapshotSchema.optional(),
    recipients: z.array(inspectionDeliveryRecipientSnapshotSchema),
    primaryRecipientId: z.string().optional(),

    deliveryChannel: z.enum(['email', 'print_mail_manual', 'post_at_site', 'hold', 'provider_mail', 'sms']),
    deliveryStatus: z.enum(['unresolved', 'ready', 'inProgress', 'delivered', 'failed', 'cancelled']),
    exceptionReason: z.string().optional(),
    exceptionReasons: z.array(z.string()).optional(),

    groupingKey: z.string(),
    groupingLabel: z.string(),
    deliveryGroupId: objectIdLikeSchema.optional(),

    deliveryAddressSnapshot: postalAddressSchema.optional(),
    deliveryEmailSnapshot: z.array(z.string()),
    deliverySmsSnapshot: z.array(z.string()).optional(),
    mailProvider: z.enum(['manual', 'stampsCom']).optional(),
    sitePostingInstructions: z.string().optional(),
    lastEmailStatus: z.string().optional(),

    reportVersionId: objectIdLikeSchema.optional(),
    reportChecksum: z.string().optional(),
    dataChangedAt: isoDateLikeSchema.optional(),

    createdAt: isoDateLikeSchema,
    createdBy: objectIdLikeSchema,
    updatedAt: isoDateLikeSchema,
    updatedBy: objectIdLikeSchema,
    _deleted: z.boolean(),
    deletedAt: isoDateLikeSchema.optional(),
    deletedBy: objectIdLikeSchema.optional(),
  })
  .strict();

/**
 * @contract inspectionDeliveryGroupDocumentSchema
 * Purpose: Optional batch-operation grouping of delivery records.
 */
export const inspectionDeliveryGroupDocumentSchema = z
  .object({
    _id: objectIdLikeSchema,
    schemaVersion: z.literal(1),
    groupingKey: z.string(),
    groupingLabel: z.string(),
    recipientSummary: z.string().optional(),
    recordIds: z.array(objectIdLikeSchema),
    deliveryChannel: z.string(),
    deliveryStatus: z.string(),
    createdAt: isoDateLikeSchema,
    createdBy: objectIdLikeSchema,
    updatedAt: isoDateLikeSchema,
    updatedBy: objectIdLikeSchema,
    _deleted: z.boolean(),
    deletedAt: isoDateLikeSchema.optional(),
    deletedBy: objectIdLikeSchema.optional(),
  })
  .strict();

/**
 * @contract generatedDocumentVersionDocumentSchema
 * Purpose: Write-once legal evidence of an exact send — blob/checksum/
 *   recipient snapshot. A regenerate/send creates another version; it never
 *   rewrites a previous one.
 */
export const generatedDocumentVersionDocumentSchema = z
  .object({
    _id: objectIdLikeSchema,
    schemaVersion: z.literal(1),
    recordId: objectIdLikeSchema,
    caseId: objectIdLikeSchema,
    eventIndex: z.number().int(),
    generatedDocumentId: objectIdLikeSchema,
    reportSource: generatedReportSourceSummarySchema,
    versionNumber: z.number(),
    reportChecksum: z.string(),
    reportTemplateId: objectIdLikeSchema.optional(),
    reportTemplateVersion: z.number().optional(),
    blobPath: z.string().optional(),
    blobUrl: z.string().optional(),
    fileName: z.string().optional(),
    fileSizeBytes: z.number().optional(),
    pageCount: z.number().optional(),
    deliveryChannel: z.string().optional(),
    recipients: z
      .array(
        z
          .object({
            name: z.string().optional(),
            email: z.string().optional(),
          })
          .strict(),
      )
      .optional(),
    source: z.enum(['send', 'regenerate', 'backfill']),
    capturedAt: isoDateLikeSchema,
    capturedBy: objectIdLikeSchema.optional(),
    sentAt: isoDateLikeSchema.optional(),
    createdAt: isoDateLikeSchema,
    updatedAt: isoDateLikeSchema,
    _deleted: z.boolean(),
    deletedAt: isoDateLikeSchema.optional(),
  })
  .strict();

// ---------------------------------------------------------------------------
// Inferred types
// ---------------------------------------------------------------------------

export type ReportTemplateDocument = z.infer<typeof reportTemplateDocumentSchema>;
export type GeneratedReportDocument = z.infer<typeof generatedReportDocumentSchema>;
export type GeneratedReportSource = z.infer<typeof generatedReportSourceSchema>;
export type InspectionDeliveryRecordDocument = z.infer<typeof inspectionDeliveryRecordDocumentSchema>;
export type InspectionDeliveryGroupDocument = z.infer<typeof inspectionDeliveryGroupDocumentSchema>;
export type GeneratedDocumentVersionDocument = z.infer<typeof generatedDocumentVersionDocumentSchema>;
